import { RunStateSchema, type RunState } from '@dissidium/contracts';
import { simulationHash, validateLedger } from '@dissidium/sim';
import { SaveCorruptError } from '../errors.ts';

/**
 * A checksummed checkpoint of one whole run: simulation state and the identity ledger in a
 * single record, so a crash can never commit one without the other. `through` is the journal
 * boundary it folds in.
 */
export type Checkpoint<S = RunState> = Readonly<{
  id: string;
  chronologyId: string;
  through: number;
  digest: string;
  state: S;
}>;

export type RollingState = Pick<RunState, 'envelope' | 'tick'>;
export type StateCodec<S> = { parse: (input: unknown) => S; hash: (state: S) => string; strip?: (state: S) => S };

export const benchCodec: StateCodec<RunState> = {
  parse: input => { const state = RunStateSchema.parse(input); validateLedger(state.continuity); return state; },
  hash: simulationHash,
};

export function encodeCheckpoint<S extends RollingState = RunState>(state: S, through: number, codec: StateCodec<S> = benchCodec as unknown as StateCodec<S>): Checkpoint<S> {
  if (!Number.isSafeInteger(through) || through < 0) throw new Error('A checkpoint needs a non-negative journal boundary');
  // A model with stripped persistence (version-3 saves) checksums the stored bytes.
  const persisted = codec.strip ? codec.strip(state) : state;
  const digest = codec.hash(persisted);
  return { id: `checkpoint:${through}:${digest}`, chronologyId: persisted.envelope.chronologyId, through, digest, state: persisted };
}

export function decodeCheckpoint<S extends RollingState = RunState>(input: unknown, codec: StateCodec<S> = benchCodec as unknown as StateCodec<S>): Checkpoint<S> {
  if (input === null || typeof input !== 'object' || Array.isArray(input)) throw new SaveCorruptError('The rolling checkpoint is missing or unreadable');
  const candidate = input as Record<string, unknown>;
  if (typeof candidate.through !== 'number' || !Number.isSafeInteger(candidate.through) || candidate.through < 0) {
    throw new SaveCorruptError('The rolling checkpoint has no valid journal boundary');
  }
  let state: S;
  try {
    state = codec.parse(candidate.state);
  } catch (error) {
    if (error instanceof SaveCorruptError) throw error;
    throw new SaveCorruptError(`The rolling checkpoint failed validation: ${error instanceof Error ? error.message : String(error)}`);
  }
  const digest = codec.hash(state);
  if (candidate.digest !== digest) throw new SaveCorruptError('The rolling checkpoint failed its checksum');
  if (candidate.chronologyId !== state.envelope.chronologyId) throw new SaveCorruptError('The rolling checkpoint belongs to another chronology');
  if (typeof candidate.id !== 'string' || !candidate.id) throw new SaveCorruptError('The rolling checkpoint has no identity');
  return { id: candidate.id, chronologyId: state.envelope.chronologyId, through: candidate.through, digest, state };
}
